// Does the canvas guarantee the contact is already in a pipeline by the time a step runs?
//
// The engine cannot answer that. `set_pipeline_stage` upserts: it sets the stage on an existing
// membership and pushes a new one otherwise, and the builder's Add/Move choice never reaches it
// (TRK-5674). So a step drawn as "Move to a stage" enrols a contact who was never in the pipeline,
// which on a `system:*` pipeline puts them in an Action Center queue they never qualified for.
//
// The only real guard is one the author drew, which is what this reads back. It is advisory: a
// "no" here means the graph does not prove membership, not that the workflow is wrong.

import { type Condition, type ConditionMatch, conditionScope } from './conditions'
import { STAGE_KEY_SEPARATOR } from './serialize'
import type { TriggerNodeData, WorkflowNode } from 'app/types/workflows'

/** Condition fields that talk about pipeline membership. `pipelineStage` is the builder's own field
 *  name; `pipelines.key` / `pipelines.stage` are the engine attributes, which templates and older
 *  saved workflows write directly (the serializer maps the former onto the latter). */
const MEMBERSHIP_FIELDS = new Set([`pipelineStage`, `pipelines.key`, `pipelines.stage`])

/** Operators that assert the contact IS on the named stage. `is_not` and `is_empty` assert the
 *  opposite, and `is_set` only says they are in some pipeline, which is not this one. */
const ASSERTING_OPERATORS = new Set([`is`, `contains`])

/** The pipeline a condition value names, if it names one. The stage picker writes the composite
 *  `<pipelineId>::<stage>` (TRK-5495); a bare stage name is a pre-TRK-5495 save and identifies no
 *  pipeline, so it proves nothing about a specific one. */
function pipelineOf(value: string | undefined): string | undefined {
  const [pipelineId, stage] = (value ?? ``).split(STAGE_KEY_SEPARATOR)
  return stage === undefined ? undefined : pipelineId || undefined
}

function assertsMembership(condition: Condition, pipelineId: string): boolean {
  return (
    condition.type === `field` &&
    MEMBERSHIP_FIELDS.has(condition.field ?? ``) &&
    ASSERTING_OPERATORS.has(condition.operator) &&
    pipelineOf(condition.value) === pipelineId
  )
}

/** Whether passing a condition group proves membership.
 *
 *  Under `all` every condition held, so one membership condition is enough. Under `any` only one
 *  condition is known to have held and we don't know which, so every alternative has to prove it.
 *  The shipped hygiene-exit template is that `any` case: one condition per working stage of the
 *  same pipeline, so whichever matched, the contact is in it. An undefined match means `all`, which
 *  is how the serializer reads it too (`splitIntoChecks` in `stepChecks.ts`, which derives each check's filterLogic from the step's match). */
export function groupProvesMembership(
  match: ConditionMatch | undefined,
  conditions: Condition[] | undefined,
  pipelineId: string,
): boolean {
  if (!conditions?.length) return false
  return match === `any`
    ? conditions.every((condition) => assertsMembership(condition, pipelineId))
    : conditions.some((condition) => assertsMembership(condition, pipelineId))
}

/** A trigger carries two condition groups with their own logic: the change-scope rows ("enrolls
 *  when") under `match`, and the state-scope rows ("only if") under `stateMatch`. Either one proving
 *  membership is enough, because both have to pass to enrol. */
function triggerProvesMembership(data: TriggerNodeData, pipelineId: string): boolean {
  const scoped = (want: `change` | `state`) => data.conditions?.filter((c) => conditionScope(c) === want)
  return (
    groupProvesMembership(data.match, scoped(`change`), pipelineId) ||
    groupProvesMembership(data.stateMatch, scoped(`state`), pipelineId)
  )
}

/** What a node itself settles for everything downstream of it, regardless of how the run got here. */
function establishesMembership(node: WorkflowNode, pipelineId: string): boolean {
  switch (node.type) {
    // Add or Move, either way the engine has written the membership by the time the next step runs.
    case `addToPipeline`:
      return node.data.pipelineId === pipelineId
    // A Filter is a single-path gate: everything past it passed the group.
    case `filter`:
      return groupProvesMembership(node.data.match, node.data.conditions, pipelineId)
    case `trigger`:
      return triggerProvesMembership(node.data, pipelineId)
    default:
      return false
  }
}

type MembershipEdge = { source: string; target: string; sourceHandle?: string | null }

/**
 * Node ids where membership in `pipelineId` is guaranteed on EVERY path from the trigger.
 *
 * A must-analysis, so it starts optimistic and shrinks to a fixpoint. Written that way rather than
 * as a backwards walk because a node can be reached by several paths and one unguarded path is
 * enough to enrol somebody; starting optimistic also means a cycle cannot manufacture a guarantee
 * that the edge entering it does not carry.
 */
export function nodesWithGuaranteedMembership(
  pipelineId: string,
  nodes: readonly WorkflowNode[],
  edges: readonly MembershipEdge[],
): Set<string> {
  const byId = new Map(nodes.map((node) => [node.id, node]))
  const incoming = new Map<string, MembershipEdge[]>()
  for (const edge of edges) incoming.set(edge.target, [...(incoming.get(edge.target) ?? []), edge])

  const guaranteed = new Map(nodes.map((node) => [node.id, true]))

  /** Whether arriving along this edge proves membership. */
  const carries = (edge: MembershipEdge): boolean => {
    const source = byId.get(edge.source)
    if (!source) return false
    if (guaranteed.get(source.id) || establishesMembership(source, pipelineId)) return true
    // A Yes/No branch proves its group only on the arm that matched. The "no" arm proves the
    // negation, and "not on one of these stages" never establishes membership.
    return (
      source.type === `branch` &&
      edge.sourceHandle === `yes` &&
      groupProvesMembership(source.data.match, source.data.conditions, pipelineId)
    )
  }

  // Values only ever go true → false here (every input to `carries` is monotone), so this settles.
  for (let pass = 0; pass <= nodes.length; pass++) {
    let changed = false
    for (const node of nodes) {
      const arriving = incoming.get(node.id) ?? []
      // Nothing arriving means the trigger, or an island that never runs. Neither has membership
      // established *on entry*; the trigger's own conditions count via `establishesMembership`.
      const next = arriving.length > 0 && arriving.every(carries)
      if (next !== guaranteed.get(node.id)) {
        guaranteed.set(node.id, next)
        changed = true
      }
    }
    if (!changed) break
  }

  return new Set([...guaranteed].filter(([, yes]) => yes).map(([id]) => id))
}
